import type { ProjectStats, ProjectListResponse, ProjectResponse } from '@shared/api';
import type { ProjectStorage, StoredProjectSummary } from '@/persistence/ProjectStorage';
import { deserializeProject, serializeProject } from '@/project/ProjectSerializer';
import { summarizeProject, type LandscapeProject, type ProjectSummary } from '@/project/Project';
import { TERRAIN_TEMPLATE_IDS, type TerrainTemplateId } from '@/terrain/TerrainTypes';
import { ApiClient, CloudError } from './ApiClient';
import type { CloudSession } from './Session';

/**
 * The project library backed by the hosted API instead of IndexedDB.
 *
 * It implements {@link ProjectStorage} exactly, so `ProjectMenu`, `AutosaveService`, and the unload
 * guard work against it untouched. Three details are decisions rather than accidents:
 *
 * **Documents are validated on read by the same code path as a file import.** `deserializeProject`
 * runs on whatever comes back from the server, exactly as it ran on whatever came back from
 * IndexedDB. The server stores documents opaquely and does not know the schema, so the client
 * staying the authority is what keeps a row written by an older build from reaching the editor
 * unchecked — and what makes a schema bump behave identically in both modes.
 *
 * **Counts travel with the save.** `summarizeProject` runs here, client-side, and the result is
 * sent alongside the document so the library screen can render without downloading anything. The
 * server never opens a document to compute them, which is what keeps it ignorant of the schema.
 *
 * **An expired session degrades rather than throws.** A 401 flips `persistent` to false and tells
 * the session object, so the UI reports "your session expired, sign in again" through the same
 * machinery that reports blocked IndexedDB — instead of every autosave rejecting into a toast.
 */

export interface CloudProjectStoreOptions {
  session: CloudSession;
  api?: ApiClient;
}

export class CloudProjectStore implements ProjectStorage {
  private readonly api: ApiClient;
  private readonly session: CloudSession;
  private sessionLost = false;

  constructor(options: CloudProjectStoreOptions) {
    this.session = options.session;
    this.api = options.api ?? options.session.api;
  }

  get persistent(): boolean {
    return !this.sessionLost;
  }

  async save(project: LandscapeProject): Promise<LandscapeProject> {
    const record = serializeProject({ ...project, updatedAt: new Date().toISOString() });
    await this.guard(() =>
      this.api.putJson(`/api/projects/${encodeURIComponent(record.id)}`, {
        id: record.id,
        name: record.name,
        document: record,
        stats: summarizeProject(record),
      }),
    );
    return record;
  }

  async summaries(): Promise<StoredProjectSummary[]> {
    const response = await this.guard(() =>
      this.api.getJson<ProjectListResponse>('/api/projects'),
    );
    if (response === null) return [];

    return response.projects.map((summary) => ({
      id: summary.id,
      name: summary.name,
      createdAt: summary.createdAt,
      updatedAt: summary.updatedAt,
      stats: toStats(summary.stats),
    }));
  }

  /**
   * The site a first visit should open onto, or null once the account has made one of its own.
   *
   * The question is answered by the server, not assembled here, because "your first visit" has to
   * mean the same thing on a second machine as it does on the first, and the only place that knows
   * whether an account has ever kept a site of its own is the account. A client-side rule would
   * additionally have to distinguish a site the user made from one they were handed, which is a
   * distinction the library screen has no reason to carry.
   *
   * Null on a lapsed session or a store that has none. Nothing about a first visit is worth an
   * error: the fallback is the blank canvas the app opened onto before this existed.
   */
  async firstRunSite(): Promise<string | null> {
    const response = await this.guard(() =>
      this.api.getJson<ProjectListResponse>('/api/projects'),
    );
    return response?.opensFirst ?? null;
  }

  /**
   * Tell the account this site is the one now open.
   *
   * Fire-and-forget by design: it runs immediately after a document the user can already see, and
   * a failed stamp costs them nothing they would notice. `guard` swallows a lapsed session for the
   * same reason every other courtesy here does.
   */
  async markOpened(id: string): Promise<void> {
    try {
      await this.guard(() =>
        this.api.postJson<void>(`/api/projects/${encodeURIComponent(id)}/opened`, {}),
      );
    } catch (error) {
      console.warn('Could not record which site is open.', error);
    }
  }

  /**
   * The site this account last had open, or null.
   *
   * Answered by the server, so it is the same answer on every machine the user signs in from --
   * which is the entire reason an account keeps this rather than the browser. Null on a lapsed
   * session or an account that has opened nothing: both mean "no resume", and neither is an error.
   */
  async resumeSite(): Promise<string | null> {
    const response = await this.guard(() =>
      this.api.getJson<ProjectListResponse>('/api/projects'),
    );
    return response?.resumeId ?? null;
  }

  async get(id: string): Promise<LandscapeProject | undefined> {
    try {
      const response = await this.api.getJson<ProjectResponse>(
        `/api/projects/${encodeURIComponent(id)}`,
      );
      return deserializeProject(response.document);
    } catch (error) {
      // A missing project is an answer, not a failure — the local store returns undefined for the
      // same case, and the caller's handling is already written for it.
      if (error instanceof CloudError && error.code === 'not_found') return undefined;
      if (error instanceof CloudError) this.noteSessionLoss(error);
      throw error;
    }
  }

  async rename(id: string, name: string): Promise<void> {
    await this.guard(() => this.api.patchJson(`/api/projects/${encodeURIComponent(id)}`, { name }));
  }

  async remove(id: string): Promise<void> {
    await this.guard(() => this.api.delete(`/api/projects/${encodeURIComponent(id)}`));
  }

  close(): void {
    // Nothing to release: HTTP holds no handle the way an open IDBDatabase does.
  }

  /**
   * Run a call, turning a lapsed session into degraded-but-usable rather than a rejection.
   *
   * Everything else — a 500, a network drop, a document over the size limit — still throws, because
   * those are conditions worth telling the user about and worth retrying. A lost session is
   * different: it is not transient, retrying cannot fix it, and the app has a working local mode to
   * fall back to.
   */
  private async guard<T>(call: () => Promise<T>): Promise<T | null> {
    try {
      const result = await call();
      this.sessionLost = false;
      return result;
    } catch (error) {
      if (error instanceof CloudError && error.needsSignIn) {
        this.noteSessionLoss(error);
        return null;
      }
      throw error;
    }
  }

  private noteSessionLoss(error: CloudError): void {
    if (!error.needsSignIn) return;
    this.sessionLost = true;
    this.session.markSignedOut();
  }
}

/**
 * Narrow the counts the server echoed back to the type the UI renders.
 *
 * The server stores stats as opaque JSON — it is not the authority on the project schema, and
 * deliberately so — which means `templateId` arrives as an arbitrary string. `getTerrainTemplate`
 * *throws* on an unknown id, so passing one through unchecked would take down the entire library
 * screen because a single row was written by a build that had a template this one does not. An
 * unrecognised id yields null instead, and the row renders with its date.
 */
function toStats(raw: ProjectStats | null): ProjectSummary | null {
  if (raw === null || typeof raw !== 'object') return null;
  if (!isTemplateId(raw.templateId)) return null;

  return {
    plantCount: count(raw.plantCount),
    speciesCount: count(raw.speciesCount),
    groupCount: count(raw.groupCount),
    siteObjectCount: count(raw.siteObjectCount),
    // Absent on a listing from a server that predates fauna — zero is the honest reading.
    faunaCount: count(raw.faunaCount ?? 0),
    tagCount: count(raw.tagCount),
    templateId: raw.templateId,
    widthMeters: count(raw.widthMeters),
    lengthMeters: count(raw.lengthMeters),
  };
}

function isTemplateId(value: unknown): value is TerrainTemplateId {
  return (
    typeof value === 'string' && (TERRAIN_TEMPLATE_IDS as readonly string[]).includes(value)
  );
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}
